// src/features/tuition/components/students/StudentList.js
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Button,
  Divider,
  Badge,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  School as SchoolIcon,
  Book as BookIcon,
  Check as CheckIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';

const StudentList = ({ 
  students = [], 
  loading = false, 
  onAddStudent, 
  onViewStudent, 
  onMenuOpen 
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }
  
  if (students.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No students found
        </Typography>
        <Typography color="text.secondary" paragraph>
          Start by adding your first student
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddStudent}
          sx={{
            backgroundColor: THEME_COLORS.primary,
            '&:hover': { backgroundColor: '#1e1c4e' },
            mt: 2
          }}
        >
          Add Student
        </Button>
      </Box>
    );
  }
  
  return (
    <Grid container spacing={3}>
      {students.map(student => (
        <Grid item sx= {{ width: { xs:'100%', sm: '48%', md:'31%' } }} key={student.id}>
          <Card 
            elevation={1} 
            sx={{ 
              borderRadius: 2, 
              transition: 'all 0.2s ease',
              height: '100%',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 4
              }
            }}
            onClick={() => onViewStudent(student)}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box display="flex" alignItems="center">
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      student.status === 'active' ? 
                        <CheckIcon sx={{ fontSize: 16, color: '#4CAF50' }} /> : 
                        <ClearIcon sx={{ fontSize: 16, color: '#F44336' }} />
                    }
                  >
                    <Avatar 
                      sx={{ 
                        width: 56, 
                        height: 56, 
                        bgcolor: THEME_COLORS.primary, 
                        mr: 2 
                      }}
                    >
                      {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                    </Avatar>
                  </Badge>
                  <Box>
                    <Typography variant="h6" fontWeight="medium">
                      {student.firstName} {student.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Grade {student.grade}
                    </Typography>
                  </Box>
                </Box>
                <Tooltip title="More options">
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onMenuOpen(e, student.id);
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  School:
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {student.school || 'Not specified'}
                </Typography>
                
                {student.email && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {student.email}
                  </Typography>
                )}
                
                {student.contactNumber && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {student.contactNumber}
                  </Typography>
                )}
              </Box>
              
              <Box mt={2}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Subjects:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {student.subjects && student.subjects.map((subject, index) => (
                    <Chip 
                      key={index} 
                      label={subject} 
                      size="small"
                      icon={<BookIcon sx={{ fontSize: '0.7rem !important' }} />}
                      sx={{ 
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '& .MuiChip-label': { fontSize: '0.75rem' }
                      }}
                    />
                  ))}
                  {(!student.subjects || student.subjects.length === 0) && (
                    <Typography variant="body2" color="text.secondary">
                      No subjects assigned
                    </Typography>
                  )}
                </Box>
              </Box>
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Since {new Date(student.startDate).toLocaleDateString()}
                </Typography>
                <Chip 
                  label={student.status === 'active' ? 'Active' : 'Inactive'} 
                  size="small"
                  sx={{ 
                    bgcolor: student.status === 'active' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                    color: student.status === 'active' ? '#4CAF50' : '#F44336'
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default StudentList;